import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Pages } from '../Pages'
import { MenuItems } from '../enums/MenuItems'
import roleApi from '../api/role/RoleApi'
import { RoleProcessingStatus } from '../api/role/models/RoleDto'
import LoadingSpinner from '../components/LoadingSpinner'
import Note from '../components/Note'
import { NoteType } from '../enums/NoteType'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faEnvelope,
    faPlusCircle,
    faPencil,
    faRotate,
    faFileExport,
} from '@fortawesome/free-solid-svg-icons'
import Button from '../components/Button'
import Section from '../components/Section'
import NoRecords from '../components/NoRecords'
import { Toaster, toast } from 'sonner'
import RoleProcessingStatusUi from '../components/RoleProcessingStatusUi'
import AddHiringTeamModal from '../components/AddHiringTeamModal'
import NetworkSeeds from '../components/NetworkSeeds'
import AddCompanyLinkModal from '../components/AddCompanyLinkModal'
import { AuthHelper } from '../auth/AuthHelper'
import { UserRoles } from '../api/tenant/models/UserDto'
import { useSafeUserData } from '../stateStores/User'
import LayoutSplitView from '../components/layout/LayoutSplitView'
import CandidateRightPane from '../components/manageRole/CandidateRightPane'
import NetworkSeedRightPane from '../components/manageRole/NetworkSeedRightPane'
import CompanyRightPane from '../components/manageRole/CompanyRightPane'
import RoleRightPane from '../components/manageRole/RoleRightPane'
import LayoutSingleView from '../components/layout/LayoutSingleView'
import { useManageRoleStore } from '../components/manageRole/stateStore/manageRoleStore'
import CandidateList from '../components/manageRole/CandidateList'
import RoleNetworkView from '../components/RoleNetworkView'
import CandidateFilters from '../components/manageRole/CandidateFilters'
import { PresentCandidateValues } from '../api/role/models/CandidateDto'

function ManageRole(): React.JSX.Element {
    const [searchParams] = useSearchParams()
    // const [roleLite, setRoleLite] = useState<RoleLiteDto>()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false)
    const [exportCsvIsLoading, setExportCsvIsLoading] = useState(false)
    const roleId = searchParams.get('id')
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState<string | undefined>()
    const { user } = useSafeUserData()
    // @ts-expect-error TS6133: 'regenerateRequired' is declared but its value is never read.
    const [regenerateRequired, setRegenerateRequired] = useState(false)
    const [regeneratingNetwork, setRegeneratingNetwork] = useState(false)
    const [selectedSeedIds, setSelectedSeedIds] = useState<string[]>([])
    const [selectedCompanySeedIds, setSelectedCompanySeedIds] = useState<
        string[]
    >([])
    const primePersonCache = useManageRoleStore(
        (state) => state.primePersonCache,
    )
    const primeCompanyCache = useManageRoleStore(
        (state) => state.primeCompanyCache,
    )
    const fetchNextCandidates = useManageRoleStore(
        (state) => state.fetchNextCandidates,
    )
    const setUser = useManageRoleStore((state) => state.setUser)
    const setRoleLite = useManageRoleStore((state) => state.setRoleLite)
    const roleLite = useManageRoleStore((state) => state.roleLite)
    const rightPaneMode = useManageRoleStore((state) => state.rightPaneMode)

    async function generateNetworkComplete() {
        setRegeneratingNetwork(false)
        // get the latest status etc.. from the role + candidates
        await refreshRole()
    }

    const refreshRole = useCallback(async () => {
        if (!roleId) {
            return
        }

        try {
            const role = (
                await roleApi.getLite({
                    roleId,
                    includeHiringTeamIds: true,
                    includeNetworkLinkIds: true,
                    includeCompanyLinkIds: true,
                })
            ).role

            const newSelectedSeedIds = role.hiringTeamIds!.concat(
                role.networkLinkIds!,
            )

            const newSelectedCompanySeedIds = role.companyLinkIds!

            // set up the state store
            setUser(user)
            setRoleLite(role)
            await primePersonCache(newSelectedSeedIds)
            await primeCompanyCache(newSelectedCompanySeedIds)

            // set up local state
            setSelectedCompanySeedIds(newSelectedCompanySeedIds)
            setSelectedSeedIds(newSelectedSeedIds)

            switch (role.status) {
                case undefined:
                case RoleProcessingStatus.ERROR:
                case RoleProcessingStatus.NOT_STARTED:
                    if (
                        newSelectedSeedIds.length > 0 ||
                        newSelectedCompanySeedIds.length > 0
                    ) {
                        setRegenerateRequired(true)
                    }
                    break
                case RoleProcessingStatus.GATHERING_CANDIDATES:
                case RoleProcessingStatus.GENERATING_NETWORK:
                case RoleProcessingStatus.CALC_PROXIMITY_BOUNDARIES:
                case RoleProcessingStatus.CALC_JOB_RELEVANCE:
                case RoleProcessingStatus.FINDING_BEST_CANDIDATES:
                    setRegeneratingNetwork(true)
                    break
                case RoleProcessingStatus.COMPLETE:
                    // get the first 50 candidates
                    await fetchNextCandidates()
                    break
                // case RoleProcessingStatus.ERROR:
                //     setErrMsg(role.errorMsg)
                //     break
                default:
                    break
            }
        } catch (error) {
            console.error(error)
            const msg =
                'There was a problem loading the role. Please refresh the page to try again. Error details: '
            if (error instanceof Error) {
                setErrMsg(`${msg}${error.message}`)
            } else {
                // Handle non-Error objects (e.g., strings or custom error shapes)
                setErrMsg(`${msg}An unknown error occurred.`)
            }
        } finally {
            setIsLoading(false)
        }
    }, [
        roleId,
        user,
        setUser,
        setRoleLite,
        fetchNextCandidates,
        primePersonCache,
        primeCompanyCache,
    ])

    useEffect(() => {
        refreshRole()
    }, [roleId, refreshRole])

    async function processRole() {
        if (!roleId) {
            return
        }

        try {
            await roleApi.processRole({ roleId })
            setRegeneratingNetwork(true)
            setRegenerateRequired(false)
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem processing the role, please try again.`,
                {
                    duration: 5000,
                },
            )
        }
    }

    async function handleRequestFeedback() {
        if (
            confirm('Email the Hiring Team on this Role to request feedback?')
        ) {
            await roleApi.sendRequestFeedbackEmails(roleId!)
        }
    }

    async function addHiringManagerModalClosed(hmsChanged: boolean) {
        if (hmsChanged) {
            await refreshRole()
        }
        setIsModalOpen(false)
    }

    async function addCompanyLinkModalClosed(companiesChanged: boolean) {
        if (companiesChanged) {
            await refreshRole()
        }
        setIsCompanyModalOpen(false)
    }

    async function exportCsv() {
        if (!roleId) {
            return
        }

        try {
            setExportCsvIsLoading(true)
            const blob = await roleApi.exportCandidatesCsv(roleId)
            const downloadUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = `contxt_data_role_${roleId}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(downloadUrl)
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem exporting the CSV, please try again.`,
                { duration: 5000 },
            )
        } finally {
            setExportCsvIsLoading(false)
        }
    }

    if (isLoading) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <LoadingSpinner />
            </LayoutSingleView>
        )
    }

    if (errMsg) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <Note type={NoteType.ERROR}>{errMsg}</Note>
            </LayoutSingleView>
        )
    }

    return (
        <LayoutSplitView
            activeMenuItem={MenuItems.Roles}
            rightPane={
                roleId && roleLite
                    ? (() => {
                          switch (rightPaneMode) {
                              case 'role':
                                  return <RoleRightPane />
                              case 'candidate':
                                  return <CandidateRightPane />
                              case 'network_seed':
                                  return <NetworkSeedRightPane />
                              case 'company':
                                  return <CompanyRightPane />
                              default:
                                  throw new Error(
                                      `Invalid mode: ${rightPaneMode}`,
                                  )
                          }
                      })()
                    : undefined
            }
        >
            <Section
                subtitle="Role"
                title={roleLite?.jobTitle}
                buttons={
                    <>
                        {roleLite &&
                            AuthHelper.userHasRole(user, UserRoles.TA) && (
                                <>
                                    <Button
                                        style="secondary"
                                        type="link"
                                        to={`${Pages.UpdateRole}?id=${roleId}`}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPencil}
                                            className={`mr-2`}
                                        />
                                        Edit
                                    </Button>
                                    {/* TODO : Do we allow searching for candidates if there are no hiring managers? */}
                                    {/* <Button
                                        style="secondary"
                                        type="link"
                                        to={`${Pages.CandidateSearchForRole}?id=${roleId}`}
                                    >
                                        <FontAwesomeIcon
                                            icon={faMagnifyingGlass}
                                            className={`mr-2`}
                                        />
                                        Search Candidates
                                    </Button> */}
                                    {roleLite.status ===
                                        RoleProcessingStatus.COMPLETE && (
                                        <>
                                            {/* <Button
                                            style="secondary"
                                            type="button"
                                            onClick={() =>
                                                setIsPresentModalOpen(true)
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faDisplay}
                                                className={`mr-2`}
                                            />
                                            Present
                                        </Button> */}
                                            <Button
                                                style="secondary"
                                                type="button"
                                                onClick={() => processRole()}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faRotate}
                                                    className={`mr-2`}
                                                />
                                                Regenerate Network
                                            </Button>
                                        </>
                                    )}
                                    {AuthHelper.userHasRole(
                                        user,
                                        UserRoles.ADMIN,
                                    ) &&
                                        roleLite.status ===
                                            RoleProcessingStatus.COMPLETE && (
                                            <Button
                                                style="secondary"
                                                type="button"
                                                onClick={exportCsv}
                                                enabled={!exportCsvIsLoading}
                                            >
                                                {exportCsvIsLoading && (
                                                    <LoadingSpinner
                                                        spinnerSize={24}
                                                    />
                                                )}
                                                {!exportCsvIsLoading && (
                                                    <FontAwesomeIcon
                                                        icon={faFileExport}
                                                    />
                                                )}
                                                <div className="ml-2">
                                                    Export CSV
                                                </div>
                                            </Button>
                                        )}
                                    {roleLite.status ===
                                        RoleProcessingStatus.COMPLETE && (
                                        <Button
                                            style="primary"
                                            type="button"
                                            onClick={handleRequestFeedback}
                                        >
                                            <FontAwesomeIcon
                                                icon={faEnvelope}
                                                className={`mr-2`}
                                            />
                                            Publish
                                        </Button>
                                    )}
                                </>
                            )}
                    </>
                }
            >
                {roleLite &&
                    selectedSeedIds.length === 0 &&
                    selectedCompanySeedIds.length === 0 && (
                        <NoRecords
                            imgSrc="connecting_teams.svg"
                            caption="Add a hiring manager to start finding quality candidates"
                        >
                            <Button
                                style="primary"
                                type="button"
                                classes="mt-4"
                                onClick={() => setIsModalOpen(true)}
                            >
                                <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    className={`mr-2`}
                                />
                                Add Hiring Manager
                            </Button>
                        </NoRecords>
                    )}

                {roleLite &&
                    (selectedSeedIds.length > 0 ||
                        selectedCompanySeedIds.length > 0) &&
                    AuthHelper.userHasRole(user, UserRoles.TA) && (
                        <>
                            {/* TODO : WHAT DO WE NAME THIS SECTIONS NOW? */}
                            <Section
                                title="Hiring Team"
                                className="mt-6"
                                titleType="h2"
                                collapsible={true}
                                defaultCollapsed={false}
                                buttons={
                                    <Button
                                        style="secondary"
                                        type="button"
                                        onClick={() => setIsModalOpen(true)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPlusCircle}
                                            className={`mr-2`}
                                        />
                                        Add Hiring Managers
                                    </Button>
                                }
                            >
                                <NetworkSeeds
                                    role={roleLite}
                                    hmRemoved={() => {
                                        refreshRole()
                                    }}
                                />
                            </Section>
                            {/* TODO : Do we want to select company links in this page? */}
                            {/* <Section
                                title="Company Links"
                                className="mt-6"
                                titleType="h2"
                                collapsible={true}
                                defaultCollapsed={false}
                                buttons={
                                    <Button
                                        style="secondary"
                                        type="button"
                                        onClick={() =>
                                            setIsCompanyModalOpen(true)
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={faPlusCircle}
                                            className={`mr-2`}
                                        />
                                        Add Company Links
                                    </Button>
                                }
                            >
                                <CompanyNetworkSeeds
                                    role={roleLite}
                                    companyRemoved={() => {
                                        refreshRole()
                                    }}
                                />
                            </Section> */}
                        </>
                    )}

                {roleId && roleLite && regeneratingNetwork && (
                    <div className="">
                        <RoleProcessingStatusUi
                            className="mt-6"
                            roleId={roleId}
                            roleFinishedCallback={generateNetworkComplete}
                        />
                    </div>
                )}

                {roleLite &&
                    roleLite.status === RoleProcessingStatus.COMPLETE &&
                    !regeneratingNetwork && (
                        <Section
                            title={`Candidates (${roleLite.candidatePoolCount?.toLocaleString()})`}
                            className="mt-6"
                            titleType="h2"
                        >
                            <RoleNetworkView />
                            <CandidateFilters
                                defaultPresentFilter={
                                    !AuthHelper.userHasRole(user, UserRoles.TA)
                                        ? PresentCandidateValues.YES
                                        : undefined
                                }
                                hidePresentFilter={
                                    !AuthHelper.userHasRole(user, UserRoles.TA)
                                }
                            />
                            <CandidateList />
                        </Section>
                    )}

                {isModalOpen && (
                    <>
                        <AddHiringTeamModal
                            roleId={roleId!}
                            currentNetworkSeedIds={selectedSeedIds}
                            onClose={addHiringManagerModalClosed}
                            showNetworkLinksButton={false}
                        />
                    </>
                )}
                {isCompanyModalOpen && (
                    <>
                        <AddCompanyLinkModal
                            roleId={roleId!}
                            selectedCompanySeedIds={selectedCompanySeedIds}
                            onClose={addCompanyLinkModalClosed}
                        />
                    </>
                )}
            </Section>
            <Toaster />
        </LayoutSplitView>
    )
}

export default ManageRole
